// กล่องแบรนด์ในหน้า My Job
import { Link } from "react-router";
import type { Brand } from "@contracts/api";
import { useAppSelector } from "@/app/hooks";
import { NoAccess } from "@features/auth/components/require-admin";
import { isAdmin } from "@features/auth/lib/roles";
import { menuPath } from "@features/navigation/model/side-menus";
import { BrandLogo } from "./brand-logo";

export function BrandGrid({ brands }: { brands: Brand[] }) {
  if (!brands.length) return <EmptyState />;

  return (
    <>
      {brands.map((b) => (
        <Link key={b.id} className="brand-card" to={menuPath(b.id, "overview")}>
          <div className="card-top">
            <BrandLogo brand={b} className="brand-logo" />
            <div>
              <h2>
                {b.name}
                {b.kind === "group" && <span className="badge badge-blue">กลุ่ม</span>}
              </h2>
              <div className="tagline">{b.tagline}</div>
            </div>
          </div>
          <div className="card-stats">
            <span>
              <b>{b.stats.campaigns}</b> Campaigns
            </span>
            <span>
              <b>{b.stats.contents}</b> Contents
            </span>
          </div>
        </Link>
      ))}
    </>
  );
}

/**
 * ว่างเปล่าเพราะอะไร ต่างกันคนละเรื่องตามสิทธิ์:
 * Admin = ยังไม่ได้ enable แบรนด์ไหนเลย (แก้เองได้)
 * คนอื่น = ยังไม่ถูกกำหนดให้เข้าถึงแบรนด์ใด — บอกให้ไป Setting ไม่มีประโยชน์ เพราะเข้าไม่ได้
 */
function EmptyState() {
  const session = useAppSelector((s) => s.session.current);

  if (!isAdmin(session)) return <NoAccess />;

  return (
    <div className="panel empty-state">
      <h3>ยังไม่มีแบรนด์ที่เปิดใช้งาน</h3>
      <p className="muted">
        ไปที่ Setting → Product แล้วกด Enable แบรนด์ที่ต้องการให้แสดงใน My Job
      </p>
      <Link className="btn-primary inline-btn" to="/setting/product">
        ไปที่ Setting
      </Link>
    </div>
  );
}

/** โหลดไม่สำเร็จ — ของเดิมได้แค่ console.warn แล้วหน้าว่างเปล่า อันนี้บอกผู้ใช้และให้ลองใหม่ */
export function BrandGridError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="panel empty-state">
      <h3>โหลดรายการแบรนด์ไม่สำเร็จ</h3>
      <p className="muted">{message}</p>
      <button type="button" className="btn-primary inline-btn" onClick={onRetry}>
        ลองอีกครั้ง
      </button>
    </div>
  );
}

/** ระหว่างโหลด — ใช้โครงการ์ดเดิมเพื่อไม่ให้ layout กระตุกตอนข้อมูลมาถึง */
export function BrandGridSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div key={i} className="brand-card is-skeleton" aria-hidden="true">
          <div className="card-top">
            <span className="brand-logo sk-box" />
            <div className="sk-lines">
              <span className="sk-line sk-line-lg" />
              <span className="sk-line" />
            </div>
          </div>
          <div className="card-stats">
            <span className="sk-line sk-line-sm" />
            <span className="sk-line sk-line-sm" />
          </div>
        </div>
      ))}
    </>
  );
}
